// import React, { useEffect, useState } from "react";
// import { Button } from "@/components/ui/button";
// import api from "@/lib/api";
// import { Lock, Unlock, MessageCircle, Mail } from "lucide-react";

// /** Renders masked contact + unlock CTA when application is accepted. */
// export default function ContactUnlock({ applicationId, status }) {
//   const [contact, setContact] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const isAccepted = status === "accepted";

//   useEffect(() => {
//     if (!isAccepted || !applicationId) return;
//     setLoading(true);
//     api.get(`/applications/${applicationId}/contact`)
//       .then(({ data }) => setContact(data))
//       .catch(() => setContact(null))
//       .finally(() => setLoading(false));
//   }, [isAccepted, applicationId]);

//   if (!isAccepted) {
//     return (
//       <div className="border border-slate-200 rounded-xl p-5 bg-slate-50" data-testid="contact-locked">
//         <div className="flex items-center gap-2 text-slate-700">
//           <Lock className="w-4 h-4" />
//           <div className="font-semibold text-sm">Contact details locked</div>
//         </div>
//         <p className="mt-2 text-sm text-slate-600">
//           Contact information is revealed only after the opportunity poster accepts the application.
//         </p>
//       </div>
//     );
//   }

//   if (loading || !contact) {
//     return <div className="border border-slate-200 rounded-xl p-5 text-sm text-slate-500">Loading contact…</div>;
//   }

//   return (
//     <div className="border border-emerald-200 rounded-xl p-5 bg-emerald-50" data-testid="contact-unlocked">
//       <div className="flex items-center gap-2 text-emerald-800">
//         <Unlock className="w-4 h-4" />
//         <div className="font-semibold text-sm">Contact details unlocked</div>
//       </div>
//       <div className="mt-3 text-sm space-y-1 text-slate-800">
//         <div><span className="text-slate-500">Name:</span> <b>{contact.name}</b></div>
//         {contact.whatsapp_number && <div><span className="text-slate-500">WhatsApp:</span> {contact.whatsapp_number}</div>}
//         {contact.email && <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-500" /> {contact.email}</div>}
//       </div>
//       {contact.whatsapp_url && (
//         <a href={contact.whatsapp_url} target="_blank" rel="noreferrer" data-testid="wa-continue">
//           <Button className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white">
//             <MessageCircle className="w-4 h-4 mr-1" /> Continue on WhatsApp
//           </Button>
//         </a>
//       )}
//     </div>
//   );
// }

//Updated code by kushal
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Lock, Unlock, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ContactUnlock({
  applicationId,
  status,
  contactUnlocked = false,
  isPoster = false,
}) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAccepted = status === "accepted";
  const isCompleted = status === "completed";
  const canLoadContact =
    (isAccepted || isCompleted) && contactUnlocked === true;

  useEffect(() => {
    if (!canLoadContact || !applicationId) {
      setContact(null);
      return;
    }

    let cancelled = false;

    setLoading(true);

    api
      .get(`/applications/${applicationId}/contact`)
      .then(({ data }) => {
        if (!cancelled) {
          setContact(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContact(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canLoadContact, applicationId]);

  if (!isAccepted && !isCompleted) {
    return (
      <div
        className="border border-slate-200 rounded-xl p-5 bg-slate-50"
        data-testid="contact-locked"
      >
        <div className="flex items-center gap-2 text-slate-700">
          <Lock className="w-4 h-4" />
          <div className="font-semibold text-sm">
            Contact details locked
          </div>
        </div>

        <p className="mt-2 text-sm text-slate-600">
          Contact information remains private until the application is
          accepted and the poster confirms contact sharing.
        </p>
      </div>
    );
  }

  if ((isAccepted || isCompleted) && !contactUnlocked) {
    return (
      <div
        className="border border-amber-200 rounded-xl p-5 bg-amber-50"
        data-testid="contact-awaiting-confirmation"
      >
        <div className="flex items-center gap-2 text-amber-800">
          <Lock className="w-4 h-4" />

          <div className="font-semibold text-sm">
            Contact details still locked
          </div>
        </div>

        <p className="mt-2 text-sm text-amber-900">
          The application has been accepted, but contact information has not
          been revealed yet.
        </p>

        {isPoster && (
          <p className="mt-2 text-xs text-amber-800">
            Contact information is revealed only after you confirm the
            acceptance.
          </p>
        )}
      </div>
    );
  }

  if (loading || !contact) {
    return (
      <div
        className="border border-slate-200 rounded-xl p-5 text-sm text-slate-500"
        data-testid="contact-loading"
      >
        Loading contact…
      </div>
    );
  }

  return (
    <div
      className="border border-emerald-200 rounded-xl p-5 bg-emerald-50"
      data-testid="contact-unlocked"
    >
      <div className="flex items-center gap-2 text-emerald-800">
        <Unlock className="w-4 h-4" />

        <div className="font-semibold text-sm">
          Contact details unlocked
        </div>
      </div>

      <div className="mt-3 text-sm space-y-1 text-slate-800">
        <div>
          <span className="text-slate-500">Name:</span>{" "}
          <b>{contact.name}</b>
        </div>

        {contact.whatsapp_number && (
          <div>
            <span className="text-slate-500">WhatsApp:</span>{" "}
            {contact.whatsapp_number}
          </div>
        )}

        {contact.email && (
          <div className="flex items-center gap-1">
            <Mail className="w-3.5 h-3.5 text-slate-500" />
            {contact.email}
          </div>
        )}
      </div>

      {contact.whatsapp_url && (
        <a
          href={contact.whatsapp_url}
          target="_blank"
          rel="noreferrer"
          data-testid="wa-continue"
        >
          <Button className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <MessageCircle className="w-4 h-4 mr-1" />
            Continue on WhatsApp
          </Button>
        </a>
      )}
    </div>
  );
}